import { Validate } from "react-hook-form"

import type { HackedPostAuthRegisterBody } from "./types"

export const LABELS = {
  email: "メールアドレス",
  phoneNumber: "電話番号",
  password: "パスワード",
  name: "氏名",
  sex: "性別",
  userSchool: "学校名",
  userFaculty: "学部・学科名",
  facultyCategoryId: "学科系統",
  year: "学年",
  introductionKey: "紹介コード",
  graduatesIn: "卒業予定年度",

  applicationCategoryIds: "興味のある活動",

  jobHuntingStatus: "就職活動状況",
  majorOccupationIdsForNewGradJobs: "興味のある職種",
  industryIds: "興味のある業界",
  companyTypeIds: "希望の企業規模",

  majorOccupationIdsForLongInternship: "興味のある職種",
  frequency: "1週間の出勤可能日数",
  frequencyWeekday: "1週間の出勤可能な平日の日数",
  availableHoursPerWeek: "1週間の合計出勤可能時間",
  period: "継続可能期間",

  studentExperienceTypeIds: "学生時代に行ったこと",
  studentAchievementTypeIds: "学生時代の実績",
  programmingExperience: "プログラミング経験",
  designExperience: "デザイン経験",
  skills: "スキル",
} as const

type ExcludeKey =
  | "nonce"
  | "redirect_after_auth"
  | "minor_occupation_ids_for_new_grad_jobs"
  | "g_recaptcha_response"
  | "utm_source"
  | "utm_medium"
  | "utm_campaign"
  | "gad_source"
  | "desired_work_area_ids"
  | "home_station_id"
  | "school_station_id"

type ValidateMap = {
  [Key in Exclude<keyof HackedPostAuthRegisterBody, ExcludeKey>]-?: Validate<HackedPostAuthRegisterBody[Key]>
}

const requiredError = (label: string) => `${label}を入力してください`
const minTextError = (length: number, label: string) => `${label}は${length}文字以上で入力してください`
const maxTextError = (length: number, label: string) => `${label}は${length}文字以内で入力してください`
const minNumberError = (n: number, label: string) => `${label}は${n}以上を入力してください`

const createValidate = <V extends ValidateMap>(validate: V) => validate

/**
 * 各要素のvalidate関数、stringを返却することでそれがエラーメッセージとなる
 *
 * createValidate()をわざわざ定義して呼び出している理由は、例えば`VALIDATE_MAP.email`に対して
 * vscodeの「実装へ移動」(cmd+F12)をした時にこのファイルを表示させるため
 */
export const VALIDATE_MAP = createValidate({
  email: (it) => {
    const maxLength = 255
    if (!it) return requiredError(LABELS.email)
    if (it.length > maxLength) return maxTextError(maxLength, LABELS.email)
    if (!/^[a-zA-Z0-9.!#$%&'*+\/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/.test(it)) {
      return `有効なメールアドレスを入力してください`
    }
  },
  password: (it) => {
    const maxLength = 255
    const minLength = 8
    if (!it) return requiredError(LABELS.password)
    if (it.length < minLength) return minTextError(minLength, LABELS.password)
    if (it.length > maxLength) return maxTextError(maxLength, LABELS.password)
  },
  name: (it) => {
    const maxLength = 64
    if (!it) return requiredError(LABELS.name)
    if (it.length > maxLength) return maxTextError(maxLength, LABELS.name)
  },
  sex: (it) => {
    if (!it) return requiredError(LABELS.sex)
  },
  user_school: (it) => {
    const maxLength = 64
    if (!it) return requiredError(LABELS.userSchool)
    if (it.length > maxLength) return maxTextError(maxLength, LABELS.userSchool)
  },
  user_faculty: (it) => {
    const maxLength = 64
    if (!it) return requiredError(LABELS.userFaculty)
    if (it.length > maxLength) return maxTextError(maxLength, LABELS.userFaculty)
  },
  faculty_category_id: (it) => {
    if (!it) return requiredError(LABELS.facultyCategoryId)
  },
  year: (it) => {
    if (!it) return requiredError(LABELS.year)
  },
  introduction_key: (it) => {
    const maxLength = 64
    if (it && it.length > maxLength) return maxTextError(maxLength, LABELS.introductionKey)
  },
  graduates_in: (it) => {
    if (!it) return requiredError(LABELS.graduatesIn)
  },
  phone_number: (it) => {
    // if (!it) return requiredError(LABELS.phoneNumber)
    if (it && !/^[0-9]{10,}$/.test(it)) return `${LABELS.phoneNumber}は半角数字で10桁以上を入力してください`
  },
  application_category_ids: (it) => {
    if (!it || it.length === 0) return requiredError(LABELS.applicationCategoryIds)
  },

  // 新卒が選択されている時
  job_hunting_status: (it) => {
    if (!it || it.length === 0) return requiredError(LABELS.jobHuntingStatus)
  },
  major_occupation_ids_for_new_grad_jobs: (it) => {
    if (!it || it.length === 0) return requiredError(LABELS.majorOccupationIdsForNewGradJobs)
  },
  industry_ids: (it) => {
    if (!it || it.length === 0) return requiredError(LABELS.industryIds)
  },
  company_type_ids: (it) => {
    if (!it || it.length === 0) return requiredError(LABELS.companyTypeIds)
  },

  // 長期インターンが選択されている時
  major_occupation_ids_for_long_internship: (it) => {
    if (!it) return requiredError(LABELS.majorOccupationIdsForLongInternship)
  },
  frequency: (it) => {
    if (!it) return requiredError(LABELS.frequency)
  },
  frequency_weekday: (it) => {
    if (!it) return requiredError(LABELS.frequencyWeekday)
  },
  available_hours_per_week: (it) => {
    const min = 1
    // if (!it) return requiredError(LABELS.availableHoursPerWeek)
    if (it && it < min) return minNumberError(1, LABELS.availableHoursPerWeek)
  },
  period: (it) => {
    if (!it) return requiredError(LABELS.period)
  },

  // 新卒か長期インターンのどちらかが選択されている時
  student_experience_type_ids: (it) => {
    if (!it || it.length === 0) return requiredError(LABELS.studentExperienceTypeIds)
  },
  student_achievement_type_ids: (it) => {
    if (!it || it.length === 0) return requiredError(LABELS.studentAchievementTypeIds)
  },
  programming_experience: (it) => {
    if (!it) return requiredError(LABELS.programmingExperience)
  },
  design_experience: (it) => {
    if (!it) return requiredError(LABELS.designExperience)
  },
  skills: (it) => {
    if (!it || it.length === 0) return `${LABELS.skills}は最低でも1つ以上入力してください`
  },
})
